<template lang="pug">
component(
  v-if="layout"
  :is="layout"
  :page="page")
</template>

<script lang="ts">
import { mapActions, mapGetters } from "vuex"
import { replaceUrlS3 } from "~/utils/imageUrlS3"
import { SET_BREADCRUMBS } from "~/store/pages"
import { SET_FW_TOKEN, SET_USER, GET_FW_TOKEN } from "~/store/auth"
import { AuthenticationService } from "~/services/public/Authentication"
import { TrackingService } from "~/services/public/Tracking"
import { PhoneNumberService } from "~/services/public/PhoneNumber"
import { CreditCodeService } from "~/services/public/CreditCode"
import { SET_CREDIT_CODE_VALUE, SET_CREDIT_CODE_LOCALE } from "~/store/credit-code"
import { SET_PHONE_NUMBER_VALUE } from "~/store/phone-number"
import { SET_LOCATION_INFO, SET_MAP_LOCATIONS } from "~/store/location"
import { SET_INTERNAL_REVIEWS } from "~/store/internal-reviews"
import { buildStructuredData } from "~/scripts/google-structured-data"
import {
  getHrefLangLinkMetaForHomePage,
  getHrefLangLinkMetaForTPPage,
  getPath,
  isHomePage,
  isTPPage
} from "~/scripts/useful-functions"
import { PageLayoutRebrand } from "#components"
import type { LinkPropertyHref } from "vue-meta/types/vue-meta"
import { LOCALE } from "~/constants/locale"

interface ComponentData {
  layout: string
  internalReviewsFilter: Record<string, any>
}

type RobotsMeta = "noindex" | "nofollow"

interface PageLayoutProvider {
  internalReviewsFilter: Record<string, any>
  layout: string
  setBreadcrumbs: (payload: { breadcrumbs: any }) => void
  replaceUrlS3: (url: string, config: any) => string
  setMapLocations: (location: any) => void
  setLocationInfo: (location: any) => void
  setInternalReviews: (reviews: any) => void
  setFwToken: (token: string) => void
  setUser: (user: any) => void
  setPhoneNumberValue: (phoneNumber: any) => void
  setCreditCodeValue: (creditCode: number) => void
  setCreditCodeLocale: (locale: string) => void
  getLayoutFromTemplate: () => string
  authenticateAndUpdatePhone: () => Promise<void>
  updatePhoneNumber: () => Promise<void>
  useCreditCode: () => Promise<void>
  sendTrackingEvents: () => Promise<void>
}

export default defineComponent({
  name: "PageLayout",
  components: {
    PageLayoutRebrand
  },

  provide() {
    return {
      internalReviewsFilter: this.internalReviewsFilter,
      trustPilotReviewsFilter: this.page?.trustpilotReviews
    }
  },

  props: {
    page: {
      type: Object as () => Page,
      required: true
    },
    noSeo: {
      type: Boolean
    }
  },

  async setup(props) {
    const route = useRoute()
    const i18n = useI18n()
    const config = useRuntimeConfig()

    const trustPilotConfig = config.public.trustPilot
    let trustPilotId = trustPilotConfig.id
    let trustPilotURL = trustPilotConfig.url
    let trustPilotAPIKey = trustPilotConfig.apiKey

    if (i18n.locale.value !== LOCALE.UK) {
      const upperCaseLocale = i18n.locale.value.toUpperCase()
      trustPilotId = trustPilotConfig[`id${upperCaseLocale}`]
      trustPilotURL = trustPilotConfig[`url${upperCaseLocale}`]
      trustPilotAPIKey = trustPilotConfig[`apiKey${upperCaseLocale}`]
    }

    const { data: trustpilotData, error } = await useFetch(
      `https://api.trustpilot.com/v1/business-units/${trustPilotId}`,
      {
        params: {
          apikey: trustPilotAPIKey
        },
        server: true,
        lazy: false
      }
    )

    const trustpilotRating = trustpilotData.value?.score?.trustScore || 0
    const trustpilotCustomers = trustpilotData.value?.numberOfReviews?.total || 0

    const headConfig: MetaInfo = (() => {
      const head: MetaInfo = { script: [] as any[] }

      const link: LinkPropertyHref[] = []
      if (isHomePage(i18n.locale.value, getPath(route))) {
        link.push(...getHrefLangLinkMetaForHomePage())
      }
      if (isTPPage(i18n.locale.value, getPath(route))) {
        link.push(...getHrefLangLinkMetaForTPPage())
      }

      if (props.page?.seo) {
        const seo = props.page.seo
        const frontendUrl = props.page.frontendUrl

        const facebookTags = [
          ...(seo.opengraphType
            ? [
                {
                  key: "og:type",
                  property: "og:type",
                  content: seo.opengraphType
                }
              ]
            : []),
          ...(seo.opengraphTitle
            ? [
                {
                  key: "og:title",
                  property: "og:title",
                  content: seo.opengraphTitle
                }
              ]
            : []),
          ...(seo.opengraphDescription
            ? [
                {
                  key: "og:description",
                  property: "og:description",
                  content: seo.opengraphDescription
                }
              ]
            : []),
          ...(frontendUrl
            ? [
                {
                  key: "og:url",
                  property: "og:url",
                  content: frontendUrl
                }
              ]
            : []),
          ...(seo.opengraphImage?.mediaDetails?.sizes?.length > 0
            ? [
                {
                  key: "og:image",
                  property: "og:image",
                  content: replaceUrlS3(
                    seo.opengraphImage.mediaDetails.sizes.find(s => s.name === "thumbnail")
                      ?.sourceUrl,
                    config.public
                  )
                }
              ]
            : [])
        ]

        const twitterTags = [
          ...(seo.twitterTitle
            ? [
                {
                  key: "twitter:title",
                  property: "twitter:title",
                  content: seo.twitterTitle
                }
              ]
            : []),
          ...(seo.twitterDescription
            ? [
                {
                  key: "twitter:description",
                  property: "twitter:description",
                  content: seo.twitterDescription
                }
              ]
            : []),
          ...(seo.twitterImage?.mediaDetails?.sizes?.length > 0
            ? [
                {
                  key: "twitter:image",
                  property: "twitter:image",
                  content: replaceUrlS3(
                    seo.twitterImage.mediaDetails.sizes.find(s => s.name === "thumbnail")
                      ?.sourceUrl,
                    config.public
                  )
                }
              ]
            : [])
        ]

        const robotsTag: RobotsMeta[] = []

        if (seo.metaRobotsNofollow) {
          robotsTag.push(seo.metaRobotsNofollow)
        }

        if (seo.metaRobotsNoindex) {
          robotsTag.push(seo.metaRobotsNoindex)
        }

        head.meta = [
          {
            key: "description",
            name: "description",
            content: seo ? seo.metaDesc : "",
            tagPriority: "critical"
          },
          ...facebookTags,
          ...twitterTags
        ]

        if (robotsTag.length > 0) {
          head.meta.push({
            key: "robots",
            name: "robots",
            content: robotsTag.join(", ")
          })
        }

        const homepageSchemaUK = JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          url: "https://www.anyvan.com",
          "@id": "https://www.anyvan.com/#website",
          name: "AnyVan",
          image: [
            "https://www.anyvan.com/content-uploads/2025/03/jan-box-in-frame.webp",
            "https://www.anyvan.com/content-uploads/2024/06/Angus-light-900px-copy-1.webp"
          ],
          about: [
            {
              "@type": "Thing",
              name: "Removals",
              alternateName: ["Removalist", "Home Removals"],
              sameAs: [
                "https://en.wikipedia.org/wiki/Moving_company",
                "https://www.wikidata.org/wiki/Q1757263",
                "https://www.wikidata.org/wiki/Q2918584",
                "https://en.wikipedia.org/wiki/Relocation_(personal)"
              ],
              url: "https://www.anyvan.com/removals"
            },
            {
              "@type": "Thing",
              name: "Man and Van",
              alternateName: ["Van Removals"],
              sameAs: ["https://en.wikipedia.org/wiki/Van", "https://en.wikipedia.org/wiki/Man"],
              url: "https://www.anyvan.com/man-and-van"
            },
            {
              "@type": "Thing",
              name: "Storage",
              alternateName: ["Self Storage"],
              sameAs: [
                "https://en.wikipedia.org/wiki/Self_storage",
                "https://www.wikidata.org/wiki/Q401404",
                "https://www.google.com/search?kgmid=/m/07xn3v"
              ],
              url: "https://www.anyvan.com/storage"
            },
            {
              "@type": "Thing",
              name: "Car Transport",
              alternateName: ["Car Delivery", "Vehicle Shipping"],
              sameAs: [
                "https://en.wikipedia.org/wiki/Car_carrier_trailer",
                "https://www.wikidata.org/wiki/Q475479",
                "https://www.google.com/search?kgmid=/m/0r4rb34",
                "https://en.wikipedia.org/wiki/Auto_carrier"
              ],
              url: "https://www.anyvan.com/vehicle-delivery/car-delivery"
            }
          ],
          mainEntity: {
            "@type": "Organization",
            name: "AnyVan",
            legalName: ["AnyVan Ltd", "AnyVan Ltd.", "AnyVan Limited"],
            "@id": "https://www.anyvan.com/#organization",
            url: "https://www.anyvan.com/",
            description:
              "AnyVan is a European online marketplace for users to obtain delivery, transport, and removal services from its community of transport partners, who must bid for the work.",
            image: { "@id": "https://www.anyvan.com/#logo" },
            logo: {
              "@type": "ImageObject",
              "@id": "https://www.anyvan.com/#logo",
              name: "AnyVan Logo",
              url: "https://www.anyvan.com/images/cust/AnyVanLogo.svg",
              contentUrl: "https://www.anyvan.com/images/cust/AnyVanLogo.svg",
              width: 140,
              height: 24
            },
            brand: {
              "@type": "Brand",
              name: "AnyVan"
            },
            foundingDate: "2009-03-04",
            sameAs: [
              "https://www.google.com/search?kgmid=/m/0fq2gmt",
              "https://www.google.com/search?kgmid=/g/1q6j3hd0z",
              "https://www.google.com/search?kgmid=/g/11s35rj2lf",
              "https://en.wikipedia.org/wiki/AnyVan",
              "https://www.wikidata.org/wiki/Q4778373",
              "https://www.facebook.com/AnyVanUK",
              "https://twitter.com/anyvan",
              "https://www.instagram.com/weareanyvan/",
              "https://www.linkedin.com/company/anyvan/",
              "https://www.crunchbase.com/organization/anyvan-com",
              "https://www.tiktok.com/@anyvan_uk",
              "https://www.youtube.com/@anyvanUK",
              "https://uk.pinterest.com/anyvan/",
              "https://99consumer.com/reviews/anyvan/",
              "https://uk.trustpilot.com/review/anyvan.com",
              "https://www.reviews.co.uk/company-reviews/store/anyvancom",
              "https://www.reviewcentre.com/Postal_and_Delivery_Services/AnyVan_-_wwwanyvancom-reviews_2323924"
            ],
            email: "info@anyvan.com",
            telephone: "+442038723050",
            acceptedPaymentMethod: ["Debit Card", "Credit Card"],
            aggregateRating: {
              "@type": "AggregateRating",
              worstRating: "1",
              bestRating: "5",
              ratingValue: trustpilotRating,
              ratingCount: trustpilotCustomers,
              url: trustPilotURL
            },
            address: {
              "@type": "PostalAddress",
              streetAddress: "5th Floor The Triangle, 5-17 Hammersmith Grove",
              addressRegion: "London",
              postalCode: "W6 0LG",
              addressCountry: "GB"
            },
            duns: "211556739",
            iso6523Code: "0060:211556739",
            identifier: [
              {
                "@type": "PropertyValue",
                propertyID: "Company Registration Number",
                value: "06837274",
                url: "https://find-and-update.company-information.service.gov.uk/company/06837274"
              },
              {
                "@type": "PropertyValue",
                propertyID: "OpenCorporates ID",
                value: "06837274",
                url: "https://opencorporates.com/companies/gb/06837274",
                sameAs: "https://en.wikipedia.org/wiki/OpenCorporates"
              }
            ],
            contactPoint: {
              "@type": "ContactPoint",
              telephone: "+442038723050",
              email: "info@anyvan.com",
              contactType: "General Enquiries",
              areaServed: "GB",
              availableLanguage: "English",
              hoursAvailable: [
                {
                  "@type": "OpeningHoursSpecification",
                  dayOfWeek: "https://schema.org/Monday",
                  opens: "08:00:00",
                  closes: "21:00:00"
                },
                {
                  "@type": "OpeningHoursSpecification",
                  dayOfWeek: "https://schema.org/Tuesday",
                  opens: "08:00:00",
                  closes: "21:00:00"
                },
                {
                  "@type": "OpeningHoursSpecification",
                  dayOfWeek: "https://schema.org/Wednesday",
                  opens: "08:00:00",
                  closes: "21:00:00"
                },
                {
                  "@type": "OpeningHoursSpecification",
                  dayOfWeek: "https://schema.org/Thursday",
                  opens: "08:00:00",
                  closes: "21:00:00"
                },
                {
                  "@type": "OpeningHoursSpecification",
                  dayOfWeek: "https://schema.org/Friday",
                  opens: "08:00:00",
                  closes: "21:00:00"
                },
                {
                  "@type": "OpeningHoursSpecification",
                  dayOfWeek: "https://schema.org/Saturday",
                  opens: "08:00:00",
                  closes: "18:00:00"
                },
                {
                  "@type": "OpeningHoursSpecification",
                  dayOfWeek: "https://schema.org/Sunday",
                  opens: "08:00:00",
                  closes: "18:00:00"
                }
              ]
            },
            parentOrganization: {
              "@type": "Corporation",
              name: "The AnyVan Group",
              legalName: "THE ANYVAN GROUP LIMITED",
              identifier: [
                {
                  "@type": "PropertyValue",
                  propertyID: "Company Registration Number",
                  value: "12617177",
                  url: "https://find-and-update.company-information.service.gov.uk/company/12617177"
                },
                {
                  "@type": "PropertyValue",
                  propertyID: "OpenCorporates ID",
                  value: "12617177",
                  url: "https://opencorporates.com/companies/gb/12617177",
                  sameAs: "https://en.wikipedia.org/wiki/OpenCorporates"
                }
              ],
              address: {
                "@type": "PostalAddress",
                streetAddress: "5th Floor The Triangle, 5-17 Hammersmith Grove",
                addressRegion: "London",
                postalCode: "W6 0LG",
                addressCountry: "GB"
              }
            }
          },
          potentialAction: {
            "@type": "QuoteAction",
            name: "Get a quote",
            description:
              "Get a quote for delivery, removal, courier and storage services in the UK and Europe",
            sameAs: [
              "https://en.wikipedia.org/wiki/Request_for_quotation",
              "https://www.wikidata.org/wiki/Q355618",
              "https://www.google.com/search?kgmid=/m/027c69c"
            ],
            target: {
              "@type": "EntryPoint",
              urlTemplate: "https://www.anyvan.com/categories"
            },
            provider: {
              "@type": "Organization",
              name: "AnyVan",
              "@id": "https://www.anyvan.com/#organization"
            }
          },
          hasPart: {
            "@type": "FAQPage",
            about: {
              "@type": "Organization",
              "@id": "https://www.anyvan.com/#organization"
            },
            author: {
              "@type": "Organization",
              "@id": "https://www.anyvan.com/#organization",
              name: "AnyVan"
            },
            mainEntity: [
              {
                "@type": "Question",
                name: "How does AnyVan work?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Great question! I founded AnyVan in 2009 with the mission to create the world's most efficient logistics technology, which would help halve the number of wasted miles driven. I noticed that a lot of vans on the roads at any given time are half full, or even entirely empty. Which makes sense. If someone is dropping off a piano from London to Cardiff, they will often have to drive back empty. We help our transport partners find extra work on the journeys they are already making, while also saving you a nice sum - as the journey was happening anyway."
                }
              },
              {
                "@type": "Question",
                name: "Does AnyVan operate near me?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "We sure do. AnyVan operates across the whole of the UK, and our transport providers are based locally meaning no matter where you are, we'll have someone in your area to assist with your move. As we've now helped several million people move, there's not many neighbourhoods we've not previously worked on, and we've most likely moved one of your neighbours already. And if you're going a bit further, we also cover moves to and within Germany, France, Spain, Italy and Ireland. "
                }
              },
              {
                "@type": "Question",
                name: "How does AnyVan offer such low removal prices?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Moving, especially if it's a whole house removal, is already expensive enough in my opinion. And I just don't think you should also be paying a fortune for a moving company, so that's why we do things a bit differently here. We're either utilising journeys that are already happening for smaller moves, so we can pass this saving on to you. Or, for larger moves we use our technology and vast knowledge of your area to cut down on inefficiencies on the day, meaning it's smoother, and cheaper - up to 40% less than others. In fact we're so confident in our ability to offer great prices, we'll also price match guarantee any removals quote you may find elsewhere."
                }
              },
              {
                "@type": "Question",
                name: "Which transport services does AnyVan offer?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "We offer a range of delivery and removal services for both residential and business customers. Whether you need to move a few boxes across town, car transport across Europe or an entire home across the country, AnyVan can handle it all. Our transport providers have vans and vehicles of all sizes to accommodate any job. See our full range of services. House removals, Storage services, Man and van, Car transport, Piano transport, Motorcycle transport, Student removals, Office removals, Furniture couriers, Vehicle delivery, International removals, Large item couriers."
                }
              },
              {
                "@type": "Question",
                name: "How do I get a van removal quote?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Getting a quote on AnyVan is easy, and should take less than 2 minutes. Just enter your moving details on our website and you'll receive an instant price. You can then book online and we'll match you with an available transport provider. Alternatively, give us a call and one of our moving experts will assist you through the whole process."
                },
                about: {
                  "@type": "Thing",
                  name: "Request a Quote",
                  description: "Request a quotation for man and van service",
                  sameAs: [
                    "https://en.wikipedia.org/wiki/Request_for_quotation",
                    "https://www.wikidata.org/wiki/Q355618",
                    "https://www.google.com/search?kgmid=/m/027c69c"
                  ]
                }
              }
            ]
          }
        })

        const removalsPageSchema = JSON.stringify({
          "@context": "https://schema.org",
          "@type": ["Product", "Service"],
          additionalType: "http://www.productontology.org/id/Moving_company",
          url: "https://www.anyvan.com/removals",
          "@id": "https://www.anyvan.com/removals/#product",
          name: "Removals",
          alternateName: ["Removalist", "Home Removals"],
          description:
            "Our teams of experienced and local movers have been busy helping over 3 million customers with everything from house removals and office removals, to international moves. You name it, we’ve got a van for that! We’ll handle all the heavy lifting (literally!) so that you can kick back knowing you’ve got five star service at a great price. Check out some of our recent moves and see what you could save!",
          image: [
            "https://www.anyvan.com/content-uploads/2024/05/HYPE-FOUR-1200x960-v4-darkerbluex2.webp",
            "https://www.anyvan.com/content-uploads/2024/05/Anyvan_2TP-BOXES-900px-1.webp",
            "https://www.anyvan.com/content-uploads/2024/10/Desktop-savings-SEO.webp",
            "https://www.anyvan.com/content-uploads/2024/05/Anyvan_sales-frames-900px.webp",
            "https://www.anyvan.com/content-uploads/2024/12/Removal-location2-copy.webp",
            "https://www.anyvan.com/content-uploads/2024/09/Desktop-other_SEO-copy.webp"
          ],
          sameAs: [
            "https://en.wikipedia.org/wiki/Moving_company",
            "https://www.wikidata.org/wiki/Q1757263",
            "https://www.wikidata.org/wiki/Q2918584",
            "https://en.wikipedia.org/wiki/Relocation_(personal)"
          ],
          additionalProperty: [
            {
              "@type": "PropertyValue",
              name: "Price Match Guarantee",
              description:
                "You could save up to 40% with AnyVan, but if you find a better price let us know and we’ll beat it!",
              value: "True"
            },
            {
              "@type": "PropertyValue",
              name: "Free cancellations",
              description:
                "Plans changed? We get it. Edit or cancel your booking up to 48 hours before your move.",
              value: "True"
            },
            {
              "@type": "PropertyValue",
              name: "Insurance Included",
              description:
                "We include £50k fire and theft cover on every booking (see T&Cs for more info).",
              value: "True"
            },
            {
              "@type": "PropertyValue",
              name: "Book Now, Pay Later",
              description:
                "Simply reserve your spot (so you get a good price!), with nothing to pay until 3 days beforehand.",
              value: "True"
            }
          ],
          audience: {
            "@type": "PeopleAudience",
            audienceType: "Adults",
            description: "People who want to move things",
            requiredMinAge: "16",
            suggestedMinAge: "18",
            suggestedMaxAge: "99",
            sameAs: "https://en.wikipedia.org/wiki/Relocation_(personal)"
          },
          offers: {
            "@type": "AggregateOffer",
            "@id": "https://www.anyvan.com/removals/#offer",
            lowPrice: 59,
            highPrice: 599,
            priceCurrency: "GBP",
            acceptedPaymentMethod: ["Credit Card", "Debit Card"],
            offeredBy: {
              "@type": "Organization",
              name: "AnyVan",
              legalName: ["AnyVan Ltd", "AnyVan Ltd.", "AnyVan Limited"],
              "@id": "https://www.anyvan.com/#organization",
              url: "https://www.anyvan.com/",
              description:
                "AnyVan is a European online marketplace for users to obtain delivery, transport, and removal services from its community of transport partners, who must bid for the work.",
              image: { "@id": "https://www.anyvan.com/#logo" },
              logo: {
                "@type": "ImageObject",
                "@id": "https://www.anyvan.com/#logo",
                name: "AnyVan Logo",
                url: "https://www.anyvan.com/images/cust/AnyVanLogo.svg",
                contentUrl: "https://www.anyvan.com/images/cust/AnyVanLogo.svg",
                width: 140,
                height: 24
              },
              brand: { "@type": "Brand", name: "AnyVan" },
              foundingDate: "2009-03-04",
              sameAs: [
                "https://www.google.com/search?kgmid=/m/0fq2gmt",
                "https://www.google.com/search?kgmid=/g/1q6j3hd0z",
                "https://www.google.com/search?kgmid=/g/11s35rj2lf",
                "https://en.wikipedia.org/wiki/AnyVan",
                "https://www.wikidata.org/wiki/Q4778373",
                "https://www.facebook.com/AnyVanUK",
                "https://twitter.com/anyvan",
                "https://www.instagram.com/weareanyvan/",
                "https://www.linkedin.com/company/anyvan/",
                "https://www.crunchbase.com/organization/anyvan-com",
                "https://www.tiktok.com/@anyvan_uk",
                "https://www.youtube.com/@anyvanUK",
                "https://uk.pinterest.com/anyvan/",
                "https://99consumer.com/reviews/anyvan/",
                "https://uk.trustpilot.com/review/anyvan.com",
                "https://www.reviews.co.uk/company-reviews/store/anyvancom",
                "https://www.reviewcentre.com/Postal_and_Delivery_Services/AnyVan_-_wwwanyvancom-reviews_2323924"
              ],
              email: "info@anyvan.com",
              telephone: "+442038723050",
              acceptedPaymentMethod: ["Debit Card", "Credit Card"],
              aggregateRating: {
                "@type": "AggregateRating",
                worstRating: "1",
                bestRating: "5",
                ratingValue: trustpilotRating,
                ratingCount: trustpilotCustomers,
                url: "https://www.trustpilot.co.uk/review/anyvan.com"
              },
              address: {
                "@type": "PostalAddress",
                streetAddress: "5th Floor The Triangle, 5-17 Hammersmith Grove",
                addressRegion: "London",
                postalCode: "W6 0LG",
                addressCountry: "GB"
              },
              duns: "211556739",
              iso6523Code: "0060:211556739",
              identifier: [
                {
                  "@type": "PropertyValue",
                  propertyID: "Company Registration Number",
                  value: "06837274",
                  url: "https://find-and-update.company-information.service.gov.uk/company/06837274"
                },
                {
                  "@type": "PropertyValue",
                  propertyID: "OpenCorporates ID",
                  value: "06837274",
                  url: "https://opencorporates.com/companies/gb/06837274",
                  sameAs: "https://en.wikipedia.org/wiki/OpenCorporates"
                }
              ]
            },
            subjectOf: {
              "@type": "WebPage",
              name: "Removals",
              url: "https://www.anyvan.com/removals",
              potentialAction: {
                "@type": "QuoteAction",
                name: "Get a quote",
                description: "Get a quote for house removals",
                sameAs: [
                  "https://en.wikipedia.org/wiki/Request_for_quotation",
                  "https://www.wikidata.org/wiki/Q355618",
                  "https://www.google.com/search?kgmid=/m/027c69c"
                ],
                target: {
                  "@type": "EntryPoint",
                  urlTemplate: "https://www.anyvan.com/instant-prices/house-move/listing-items"
                },
                provider: {
                  "@type": "Organization",
                  name: "AnyVan",
                  "@id": "https://www.anyvan.com/#organization"
                }
              },
              mainContentOfPage: {
                "@type": "Table",
                name: "How much do house removals cost?",
                description:
                  "Moving your family to a castle in the country is a little different to downsizing to a duplex for your retirement? Every move is unique, so home removals costs in the UK can vary a lot depending on where you are, how far you’re moving and how many items you have. You can usually expect to spend between £300 and £1,500.",
                cssSelector: {
                  "@type": "CssSelectorType",
                  "@value": "#comparison-block-1 > figure > table"
                }
              },
              about: {
                "@type": ["Product", "Service"],
                name: "Removals",
                "@id": "https://www.anyvan.com/removals/#product",
                url: "https://www.anyvan.com/removals"
              },
              mentions: [
                {
                  "@type": "Thing",
                  name: "Door-To-Door Service",
                  alternateName: "Door to Door Service",
                  sameAs: [
                    "https://www.wikidata.org/wiki/Q5297213",
                    "https://dhl-freight-connections.com/en/logistics-dictionary/door-to-door/#:~:text=Door%2Dto%2Ddoor%20is%20a,house%2Dto%2Dhouse%20service."
                  ]
                },
                {
                  "@type": "Thing",
                  name: "Insurance",
                  sameAs: [
                    "https://www.wikidata.org/wiki/Q43183",
                    "https://en.wikipedia.org/wiki/Insurance",
                    "https://www.google.com/search?kgmid=/m/03xks",
                    "https://www.wikidata.org/wiki/Q17093209"
                  ]
                },
                {
                  "@type": "Thing",
                  name: "House",
                  alternateName: "Home",
                  sameAs: [
                    "https://en.wikipedia.org/wiki/House",
                    "https://www.wikidata.org/wiki/Q3947",
                    "https://www.google.com/search?kgmid=/m/03jm5"
                  ]
                },
                {
                  "@type": "Thing",
                  name: "Apartment",
                  alternateName: "Flat",
                  sameAs: [
                    "https://en.wikipedia.org/wiki/Apartment",
                    "https://www.wikidata.org/wiki/Q188507",
                    "https://www.google.com/search?kgmid=/m/01nblt"
                  ]
                }
              ],
              hasPart: {
                "@type": "FAQPage",
                breadcrumb: {
                  "@type": "BreadcrumbList",
                  itemListElement: [
                    {
                      "@type": "ListItem",
                      position: 1,
                      name: "Home",
                      item: "https://www.anyvan.com"
                    },
                    { "@type": "ListItem", position: 2, name: "Removals" }
                  ]
                },
                about: {
                  "@type": ["Product", "Service"],
                  name: "Removals",
                  "@id": "https://www.anyvan.com/removals/#product",
                  url: "https://www.anyvan.com/removals"
                },
                author: {
                  "@type": "Organization",
                  "@id": "https://www.anyvan.com/#organization",
                  name: "AnyVan"
                },
                mainEntity: [
                  {
                    "@type": "Question",
                    name: "How can I get a quote for my removal?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "It takes as little as 2 minutes to get a house removal quote with us. You can get an instant price online here, or give us a call on 020 3872 3050",
                      about: {
                        "@type": "Thing",
                        name: "Request a Quote",
                        description: "Request a quotation for man and van service",
                        sameAs: [
                          "https://en.wikipedia.org/wiki/Request_for_quotation",
                          "https://www.wikidata.org/wiki/Q355618",
                          "https://www.google.com/search?kgmid=/m/027c69c"
                        ]
                      }
                    }
                  },
                  {
                    "@type": "Question",
                    name: "What factors affect the cost of removals services?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "Our removals service costs start from just £59, but getting an accurate removals quote depends on a few things. We’re an inventory based removals company, so we need to know exactly what, where and when you’re moving. This will help us figure out what size of van you need and how far it’s travelling. The season and day of the week you choose will also impact the price - if you can plan for it, moving midweek is usually most affordable!"
                    }
                  },
                  {
                    "@type": "Question",
                    name: "Does the removal service come with insurance?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "It does indeed! For added peace of mind £50k fire and theft cover is included on every removals service (see T&Cs for more info).",
                      about: {
                        "@type": "Thing",
                        name: "Insurance",
                        sameAs: [
                          "https://www.wikidata.org/wiki/Q43183",
                          "https://en.wikipedia.org/wiki/Insurance",
                          "https://www.google.com/search?kgmid=/m/03xks",
                          "https://www.wikidata.org/wiki/Q17093209"
                        ]
                      }
                    }
                  },
                  {
                    "@type": "Question",
                    name: "Do you offer packing services?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "Yes! For a truly stress-free home removal choose our Premium package and our friendly removalists will take care of the packing. We’ll even bring our own packing materials - one less thing to worry about!"
                    },
                    about: {
                      "@type": "Thing",
                      name: "Packing Materials",
                      description: "Materials to ensure items are safe during transportation",
                      sameAs: [
                        "https://www.wikidata.org/wiki/Q66157003",
                        "https://www.google.com/search?kgmid=/g/121qg2_7",
                        "https://www.google.com/search?kgmid=/g/12111nx8"
                      ]
                    }
                  },
                  {
                    "@type": "Question",
                    name: "Do you do student moves?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "Yes! We help loads of students move. Whether you’re moving into halls, or out of your uni house, AnyVan moves are budget-friendly so you don’t have to worry about breaking the bank."
                    },
                    about: {
                      "@type": "Thing",
                      name: "Student",
                      sameAs: [
                        "https://www.wikidata.org/wiki/Q48282",
                        "https://en.wikipedia.org/wiki/Student",
                        "https://www.google.com/search?kgmid=/m/014cnc"
                      ]
                    }
                  }
                ]
              }
            }
          }
        })

        const manAndVanSchema = JSON.stringify({
          "@context": "https://schema.org",
          "@type": ["Product", "Service"],
          additionalType: "http://www.productontology.org/id/Moving_company",
          url: "https://www.anyvan.com/man-and-van",
          "@id": "https://www.anyvan.com/man-and-van/#product",
          name: "Man and Van",
          alternateName: ["Man With A Van", "Man and Van Hire"],
          description:
            "Whatever you need, we have a van for that. With our man and van service, you don’t need to rent a van. Or carry everything alone. Our man and van service includes unloading and loading. Let's share the load. Tell us what you’re moving here and we’ll match you with the right service!",
          image: [
            "https://www.anyvan.com/content-uploads/2024/07/HYPE-FOUR-FURNITURE-v4-5-copy.webp",
            "https://www.anyvan.com/content-uploads/2024/05/Anyvan_2TP-BOXES-900px-1.webp",
            "https://www.anyvan.com/content-uploads/2024/09/Desktop-other_SEO-copy.webp",
            "https://www.anyvan.com/content-uploads/2024/05/Anyvan_sales-frames-900px.webp",
            "https://www.anyvan.com/content-uploads/2024/05/Frame-3van-yellowbackground-900px.webp"
          ],
          sameAs: [
            "https://en.wikipedia.org/w/index.php?title=Man_and_van&redirect=no",
            "https://en.wikipedia.org/wiki/Van",
            "https://en.wikipedia.org/wiki/Man",
            "https://www.google.com/search?kgmid=/g/1thf812m"
          ],
          additionalProperty: [
            {
              "@type": "PropertyValue",
              name: "Price Match Guarantee",
              description:
                "Customers can save up to 40% with AnyVan, but if you find a better price let us know and we’ll beat it!",
              value: "True"
            },
            {
              "@type": "PropertyValue",
              name: "Free cancellations",
              description:
                "Plans changed? Edit or cancel your booking up to 48 hours before your move, with pay nothing until 3 days before",
              value: "True"
            },
            {
              "@type": "PropertyValue",
              name: "Insurance Included",
              description:
                "We include £50k fire and theft cover on every booking (see T&Cs for more info).",
              value: "True"
            },
            {
              "@type": "PropertyValue",
              name: "Flexible Move Dates",
              description:
                "With over 5,000 transport partners we have the best man and van availability, making sure your move happens!",
              value: "True"
            }
          ],
          audience: {
            "@type": "PeopleAudience",
            audienceType: "Adults",
            description: "People who need a man and van",
            requiredMinAge: "16",
            suggestedMinAge: "18",
            suggestedMaxAge: "99"
          },
          offers: {
            "@type": "AggregateOffer",
            "@id": "https://www.anyvan.com/man-and-van/#offer",
            lowPrice: 26.0,
            priceCurrency: "GBP",
            acceptedPaymentMethod: ["Credit Card", "Debit Card"],
            offeredBy: {
              "@type": "Organization",
              name: "AnyVan",
              legalName: ["AnyVan Ltd", "AnyVan Ltd.", "AnyVan Limited"],
              "@id": "https://www.anyvan.com/#organization",
              url: "https://www.anyvan.com/",
              description:
                "AnyVan is a European online marketplace for users to obtain delivery, transport, and removal services from its community of transport partners, who must bid for the work.",
              image: { "@id": "https://www.anyvan.com/#logo" },
              logo: {
                "@type": "ImageObject",
                "@id": "https://www.anyvan.com/#logo",
                name: "AnyVan Logo",
                url: "https://www.anyvan.com/images/cust/AnyVanLogo.svg",
                contentUrl: "https://www.anyvan.com/images/cust/AnyVanLogo.svg",
                width: 140,
                height: 24
              },
              brand: { "@type": "Brand", name: "AnyVan" },
              foundingDate: "2009-03-04",
              sameAs: [
                "https://www.google.com/search?kgmid=/m/0fq2gmt",
                "https://www.google.com/search?kgmid=/g/1q6j3hd0z",
                "https://www.google.com/search?kgmid=/g/11s35rj2lf",
                "https://en.wikipedia.org/wiki/AnyVan",
                "https://www.wikidata.org/wiki/Q4778373",
                "https://www.facebook.com/AnyVanUK",
                "https://twitter.com/anyvan",
                "https://www.instagram.com/weareanyvan/",
                "https://www.linkedin.com/company/anyvan/",
                "https://www.crunchbase.com/organization/anyvan-com",
                "https://www.tiktok.com/@anyvan_uk",
                "https://www.youtube.com/@anyvanUK",
                "https://uk.pinterest.com/anyvan/",
                "https://99consumer.com/reviews/anyvan/",
                "https://uk.trustpilot.com/review/anyvan.com",
                "https://www.reviews.co.uk/company-reviews/store/anyvancom",
                "https://www.reviewcentre.com/Postal_and_Delivery_Services/AnyVan_-_wwwanyvancom-reviews_2323924"
              ],
              email: "info@anyvan.com",
              telephone: "+442038723050",
              acceptedPaymentMethod: ["Debit Card", "Credit Card"],
              aggregateRating: {
                "@type": "AggregateRating",
                worstRating: "1",
                bestRating: "5",
                ratingValue: trustpilotRating,
                ratingCount: trustpilotCustomers,
                url: "https://www.trustpilot.co.uk/review/anyvan.com"
              },
              address: {
                "@type": "PostalAddress",
                streetAddress: "5th Floor The Triangle, 5-17 Hammersmith Grove",
                addressRegion: "London",
                postalCode: "W6 0LG",
                addressCountry: "GB"
              },
              duns: "211556739",
              iso6523Code: "0060:211556739",
              identifier: [
                {
                  "@type": "PropertyValue",
                  propertyID: "Company Registration Number",
                  value: "06837274",
                  url: "https://find-and-update.company-information.service.gov.uk/company/06837274"
                },
                {
                  "@type": "PropertyValue",
                  propertyID: "OpenCorporates ID",
                  value: "06837274",
                  url: "https://opencorporates.com/companies/gb/06837274",
                  sameAs: "https://en.wikipedia.org/wiki/OpenCorporates"
                }
              ]
            },
            subjectOf: {
              "@type": "WebPage",
              name: "Man and Van",
              url: "https://www.anyvan.com/man-and-van",
              potentialAction: {
                "@type": "QuoteAction",
                name: "Get a quote",
                description: "Get a quote for a man with a van",
                sameAs: [
                  "https://en.wikipedia.org/wiki/Request_for_quotation",
                  "https://www.wikidata.org/wiki/Q355618",
                  "https://www.google.com/search?kgmid=/m/027c69c"
                ],
                target: {
                  "@type": "EntryPoint",
                  urlTemplate: "https://www.anyvan.com/instant-prices/furniture/listing-items"
                },
                provider: {
                  "@type": "Organization",
                  name: "AnyVan",
                  "@id": "https://www.anyvan.com/#organization"
                }
              },
              mainContentOfPage: {
                "@type": "Table",
                name: "See how our man and van prices compare",
                description:
                  "As the UK's most trusted mover, you can be sure you're in safe hands with AnyVan. And, to top it all off, we offer man and van services that are up to 40% cheaper than the competition. Of course, your man and van quote can depend on a few factors. But, here's how much it costs (on average!) to move some of your neighbour's most popular items.",
                cssSelector: {
                  "@type": "CssSelectorType",
                  "@value": "#comparison-block-1 > figure > table"
                }
              },
              about: {
                "@type": ["Product", "Service"],
                name: "Man and Van",
                "@id": "https://www.anyvan.com/man-and-van/#product",
                url: "https://www.anyvan.com/man-and-van"
              },
              mentions: [
                {
                  "@type": "Thing",
                  name: "Sofa",
                  alternateName: ["Settee", "Couch"],
                  sameAs: [
                    "https://en.wikipedia.org/wiki/Couch",
                    "https://www.wikidata.org/wiki/Q131514",
                    "https://www.google.com/search?kgmid=/m/02crq1"
                  ]
                },
                {
                  "@type": "Thing",
                  name: "Unloading",
                  description:
                    "discharge a load, a freight or other goods from a transport vehicle or similar",
                  alternateName: "Unload",
                  sameAs: ["https://www.wikidata.org/wiki/Q12889429"]
                },
                {
                  "@type": "Thing",
                  name: "Loading",
                  alternateName: "Load",
                  description: "lifting a load up or putting it into something for transport",
                  sameAs: ["https://www.wikidata.org/wiki/Q69466762"]
                },
                {
                  "@type": "Thing",
                  name: "Insurance",
                  sameAs: [
                    "https://www.wikidata.org/wiki/Q43183",
                    "https://en.wikipedia.org/wiki/Insurance",
                    "https://www.google.com/search?kgmid=/m/03xks",
                    "https://www.wikidata.org/wiki/Q17093209"
                  ]
                }
              ],
              hasPart: {
                "@type": "FAQPage",
                breadcrumb: {
                  "@type": "BreadcrumbList",
                  itemListElement: [
                    {
                      "@type": "ListItem",
                      position: 1,
                      name: "Home",
                      item: "https://www.anyvan.com"
                    },
                    { "@type": "ListItem", position: 2, name: "Man and Van" }
                  ]
                },
                about: {
                  "@type": ["Product", "Service"],
                  name: "Man and Van",
                  "@id": "https://www.anyvan.com/man-and-van/#product",
                  url: "https://www.anyvan.com/man-and-van",
                  sameAs: [
                    "https://en.wikipedia.org/w/index.php?title=Man_and_van&redirect=no",
                    "https://en.wikipedia.org/wiki/Van",
                    "https://en.wikipedia.org/wiki/Man",
                    "https://www.google.com/search?kgmid=/g/1thf812m"
                  ]
                },
                author: {
                  "@type": "Organization",
                  "@id": "https://www.anyvan.com/#organization",
                  name: "AnyVan"
                },
                mainEntity: [
                  {
                    "@type": "Question",
                    name: "How can I get an online quote for a man with a van near me?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "Yes, you can! It takes less than 2 minutes with our simple online form to get an instant quote for a local man with a van service. All you need to hand is three pieces of info:",
                      about: [
                        {
                          "@type": ["Product", "Service"],
                          name: "Man and Van",
                          "@id": "https://www.anyvan.com/man-and-van/#product",
                          url: "https://www.anyvan.com/man-and-van",
                          sameAs: [
                            "https://en.wikipedia.org/w/index.php?title=Man_and_van&redirect=no",
                            "https://en.wikipedia.org/wiki/Van",
                            "https://en.wikipedia.org/wiki/Man",
                            "https://www.google.com/search?kgmid=/g/1thf812m"
                          ]
                        },
                        {
                          "@type": "Thing",
                          name: "Request a Quote",
                          description: "Request a quotation for man and van service",
                          sameAs: [
                            "https://en.wikipedia.org/wiki/Request_for_quotation",
                            "https://www.wikidata.org/wiki/Q355618",
                            "https://www.google.com/search?kgmid=/m/027c69c"
                          ]
                        }
                      ]
                    }
                  },
                  {
                    "@type": "Question",
                    name: "Should I book a man with a van in advance?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "Booking your man and van service in advance means you’re more likely to get a better price, as well as choice of dates and time slots. But we know things come up at the last minute and plans change! Luckily, at AnyVan we’re available 7 days a week, including bank holidays. This even includes same-day and same-week services if that’s what you need."
                    }
                  },
                  {
                    "@type": "Question",
                    name: "Does a man with a van service include help with packing and unpacking?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "Yes! Our helpful mover will assist you in unloading and loading your items as part of our man and van service. But, they won’t pack your items. If you’re moving a large number of items you may need our removals service, which can include packing at an extra cost.",
                      about: [
                        {
                          "@type": "Thing",
                          name: "Unloading",
                          description:
                            "Discharge a load, a freight or other goods from a van or similar",
                          alternateName: "Unload",
                          sameAs: ["https://www.wikidata.org/wiki/Q12889429"]
                        },
                        {
                          "@type": "Thing",
                          name: "Loading",
                          alternateName: "Load",
                          description: "Lifting a load up or putting it into a van for transport",
                          sameAs: ["https://www.wikidata.org/wiki/Q69466762"]
                        },
                        {
                          "@type": "Thing",
                          name: "Packing Materials",
                          description: "Materials to ensure items are safe during transportation",
                          sameAs: [
                            "https://www.wikidata.org/wiki/Q66157003",
                            "https://www.google.com/search?kgmid=/g/121qg2_7",
                            "https://www.google.com/search?kgmid=/g/12111nx8"
                          ]
                        }
                      ]
                    }
                  },
                  {
                    "@type": "Question",
                    name: "What is the difference between a man and van and a removals company?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "The main differences between a man with a van company and a removals company are the size of the move they carry out and the cost. Both services will include the vehicle hire, loading and unloading, but removal companies usually also offer packing or furniture dismantling/reassembly.",
                      about: [
                        {
                          "@type": "Thing",
                          name: "Moving Company",
                          alternateName: "Removals Company",
                          sameAs: [
                            "https://en.wikipedia.org/wiki/Moving_company",
                            "https://www.wikidata.org/wiki/Q1757263",
                            "https://www.wikidata.org/wiki/Q2918584",
                            "https://en.wikipedia.org/wiki/Relocation_(personal)"
                          ]
                        },
                        {
                          "@type": "Thing",
                          name: "Man and Van",
                          sameAs: [
                            "https://en.wikipedia.org/w/index.php?title=Man_and_van&redirect=no",
                            "https://en.wikipedia.org/wiki/Van",
                            "https://en.wikipedia.org/wiki/Man",
                            "https://www.google.com/search?kgmid=/g/1thf812m"
                          ]
                        }
                      ]
                    }
                  },
                  {
                    "@type": "Question",
                    name: "Can I travel with a man and a van?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "Unfortunately, customers cannot travel with our man and van service. This is for safety and legal reasons (boo!). If you’d like to be there for the pick up and drop off, you’ll need to arrange your own travel."
                    }
                  },
                  {
                    "@type": "Question",
                    name: "Are there any additional charges?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "Nope! Nada! The cost of a man and a van includes petrol and VAT. Just make sure you have included everything in your inventory, have everything ready on time for collection and let us know if the team needs to navigate any stairs or lifts.",
                      about: {
                        "@type": "Thing",
                        name: "Additional Charges",
                        sameAs: [
                          "https://www.wikidata.org/wiki/Q230696",
                          "https://www.google.com/search?kgmid=/g/120x5qy6"
                        ]
                      }
                    }
                  }
                ]
              }
            }
          }
        })

        const heavyAndLargeItemCouriersSchema = JSON.stringify({
          "@context": "https://schema.org",
          "@type": ["Product", "Service"],
          additionalType: "http://www.productontology.org/id/Moving_company",
          url: "https://www.anyvan.com/courier-services/heavy-large-item-couriers",
          "@id": "https://www.anyvan.com/courier-services/heavy-large-item-couriers",
          name: "Heavy & Large Item Couriers",
          alternateName: ["Heavy Item Couriers", "Large Item Couriers"],
          image: [
            "https://www.anyvan.com/content-uploads/2024/07/HYPE-FOUR-FURNITURE-v4-5-copy.webp",
            "https://www.anyvan.com/content-uploads/2024/05/Anyvan_2TP-BOXES-900px-1.webp",
            "https://www.anyvan.com/content-uploads/2024/09/Desktop-other_SEO-copy.webp",
            "https://www.anyvan.com/content-uploads/2024/05/Anyvan_sales-frames-900px.webp",
            "https://www.anyvan.com/content-uploads/2024/05/Frame-3van-yellowbackground-900px.webp"
          ],
          description:
            "Save money on heavy and large item courier services with AnyVan. ✓ Trusted moving experts ✓ Low costs ✓ Anywhere in the UK.",
          additionalProperty: [
            {
              "@type": "PropertyValue",
              name: "Price Match Guarantee",
              description:
                "Customers can save up to 40% with AnyVan, but if you find a better price let us know and we’ll beat it!",
              value: "True"
            },
            {
              "@type": "PropertyValue",
              name: "Free cancellations",
              description:
                "Plans changed? Edit or cancel your booking up to 48 hours before your move, with pay nothing until 3 days before",
              value: "True"
            },
            {
              "@type": "PropertyValue",
              name: "Insurance Included",
              description:
                "We include £50k fire and theft cover on every booking (see T&Cs for more info).",
              value: "True"
            },
            {
              "@type": "PropertyValue",
              name: "Flexible Move Dates",
              description:
                "With over 5,000 transport partners we have the best man and van availability, making sure your move happens!",
              value: "True"
            }
          ],
          audience: {
            "@type": "PeopleAudience",
            audienceType: "Adults",
            description:
              "Save money on heavy and large item courier services with AnyVan. ✓ Trusted moving experts ✓ Low costs ✓ Anywhere in the UK.",
            requiredMinAge: "16",
            suggestedMinAge: "18",
            suggestedMaxAge: "99"
          },
          offers: {
            "@type": "AggregateOffer",
            "@id": "https://www.anyvan.com/man-and-van/#offer",
            lowPrice: 26,
            priceCurrency: "GBP",
            acceptedPaymentMethod: ["Credit Card", "Debit Card"],
            offeredBy: {
              "@type": "Organization",
              name: "AnyVan",
              legalName: ["AnyVan Ltd", "AnyVan Ltd.", "AnyVan Limited"],
              "@id": "https://www.anyvan.com/#organization",
              url: "https://www.anyvan.com/",
              description:
                "AnyVan is a European online marketplace for users to obtain delivery, transport, and removal services from its community of transport partners, who must bid for the work.",
              image: { "@id": "https://www.anyvan.com/#logo" },
              logo: {
                "@type": "ImageObject",
                "@id": "https://www.anyvan.com/#logo",
                name: "AnyVan Logo",
                url: "https://www.anyvan.com/images/cust/AnyVanLogo.svg",
                contentUrl: "https://www.anyvan.com/images/cust/AnyVanLogo.svg",
                width: 140,
                height: 24
              },
              brand: { "@type": "Brand", name: "AnyVan" },
              foundingDate: "2009-03-04",
              sameAs: [
                "https://www.google.com/search?kgmid=/m/0fq2gmt",
                "https://www.google.com/search?kgmid=/g/1q6j3hd0z",
                "https://www.google.com/search?kgmid=/g/11s35rj2lf",
                "https://en.wikipedia.org/wiki/AnyVan",
                "https://www.wikidata.org/wiki/Q4778373",
                "https://www.facebook.com/AnyVanUK",
                "https://twitter.com/anyvan",
                "https://www.instagram.com/weareanyvan/",
                "https://www.linkedin.com/company/anyvan/",
                "https://www.crunchbase.com/organization/anyvan-com",
                "https://www.tiktok.com/@anyvan_uk",
                "https://www.youtube.com/@anyvanUK",
                "https://uk.pinterest.com/anyvan/",
                "https://99consumer.com/reviews/anyvan/",
                "https://uk.trustpilot.com/review/anyvan.com",
                "https://www.reviews.co.uk/company-reviews/store/anyvancom",
                "https://www.reviewcentre.com/Postal_and_Delivery_Services/AnyVan_-_wwwanyvancom-reviews_2323924"
              ],
              email: "info@anyvan.com",
              telephone: "+442038723050",
              acceptedPaymentMethod: ["Debit Card", "Credit Card"],
              aggregateRating: {
                "@type": "AggregateRating",
                worstRating: "1",
                bestRating: "5",
                ratingValue: trustpilotRating,
                ratingCount: trustpilotCustomers,
                url: "https://www.trustpilot.co.uk/review/anyvan.com"
              },
              address: {
                "@type": "PostalAddress",
                streetAddress: "5th Floor The Triangle, 5-17 Hammersmith Grove",
                addressRegion: "London",
                postalCode: "W6 0LG",
                addressCountry: "GB"
              },
              duns: "211556739",
              iso6523Code: "0060:211556739",
              identifier: [
                {
                  "@type": "PropertyValue",
                  propertyID: "Company Registration Number",
                  value: "06837274",
                  url: "https://find-and-update.company-information.service.gov.uk/company/06837274"
                },
                {
                  "@type": "PropertyValue",
                  propertyID: "OpenCorporates ID",
                  value: "06837274",
                  url: "https://opencorporates.com/companies/gb/06837274",
                  sameAs: "https://en.wikipedia.org/wiki/OpenCorporates"
                }
              ]
            },
            subjectOf: {
              "@type": "WebPage",
              name: "Heavy & Large Item Couriers",
              url: "https://www.anyvan.com/man-and-van",
              potentialAction: {
                "@type": "QuoteAction",
                name: "Get a quote",
                description:
                  "Save money on heavy and large item courier services with AnyVan. ✓ Trusted moving experts ✓ Low costs ✓ Anywhere in the UK.",
                sameAs: [
                  "https://en.wikipedia.org/wiki/Request_for_quotation",
                  "https://www.wikidata.org/wiki/Q355618",
                  "https://www.google.com/search?kgmid=/m/027c69c"
                ],
                target: {
                  "@type": "EntryPoint",
                  urlTemplate: "https://www.anyvan.com/instant-prices/furniture/listing-items"
                },
                provider: {
                  "@type": "Organization",
                  name: "AnyVan",
                  "@id": "https://www.anyvan.com/#organization"
                }
              },
              about: {
                "@type": ["Product", "Service"],
                name: "Heavy & Large Item Couriers",
                "@id": "https://www.anyvan.com/courier-services/heavy-large-item-couriers",
                url: "https://www.anyvan.com/courier-services/heavy-large-item-couriers"
              },
              mentions: [
                {
                  "@type": "Thing",
                  name: "Sofa",
                  alternateName: ["Settee", "Couch"],
                  sameAs: [
                    "https://en.wikipedia.org/wiki/Couch",
                    "https://www.wikidata.org/wiki/Q131514",
                    "https://www.google.com/search?kgmid=/m/02crq1"
                  ]
                },
                {
                  "@type": "Thing",
                  name: "Unloading",
                  description:
                    "discharge a load, a freight or other goods from a transport vehicle or similar",
                  alternateName: "Unload",
                  sameAs: ["https://www.wikidata.org/wiki/Q12889429"]
                },
                {
                  "@type": "Thing",
                  name: "Loading",
                  alternateName: "Load",
                  description: "lifting a load up or putting it into something for transport",
                  sameAs: ["https://www.wikidata.org/wiki/Q69466762"]
                },
                {
                  "@type": "Thing",
                  name: "Insurance",
                  sameAs: [
                    "https://www.wikidata.org/wiki/Q43183",
                    "https://en.wikipedia.org/wiki/Insurance",
                    "https://www.google.com/search?kgmid=/m/03xks",
                    "https://www.wikidata.org/wiki/Q17093209"
                  ]
                }
              ]
            }
          }
        })

        const vehicleDeliverySchema = JSON.stringify({
          "@context": "https://schema.org",
          "@type": ["Product", "Service"],
          additionalType: "http://www.productontology.org/id/Moving_company",
          url: "https://www.anyvan.com/man-and-van",
          "@id": "https://www.anyvan.com/man-and-van/#product",
          name: "Car transport services",
          alternateName: ["Car services", "Transport services"],
          image: [
            "https://www.anyvan.com/content-uploads/2024/07/HYPE-FOUR-FURNITURE-v4-5-copy.webp",
            "https://www.anyvan.com/content-uploads/2024/05/Anyvan_2TP-BOXES-900px-1.webp",
            "https://www.anyvan.com/content-uploads/2024/09/Desktop-other_SEO-copy.webp",
            "https://www.anyvan.com/content-uploads/2024/05/Anyvan_sales-frames-900px.webp",
            "https://www.anyvan.com/content-uploads/2024/05/Frame-3van-yellowbackground-900px.webp"
          ],
          description:
            "No matter the make or state of your car, arranging collection and delivery couldn’t be easier with AnyVan’s car transport service.",
          additionalProperty: [
            {
              "@type": "PropertyValue",
              name: "Price Match Guarantee",
              description:
                "Customers can save up to 40% with AnyVan, but if you find a better price let us know and we’ll beat it!",
              value: "True"
            },
            {
              "@type": "PropertyValue",
              name: "Free cancellations",
              description:
                "Plans changed? Edit or cancel your booking up to 48 hours before your move, with pay nothing until 3 days before",
              value: "True"
            },
            {
              "@type": "PropertyValue",
              name: "Insurance Included",
              description:
                "We include £50k fire and theft cover on every booking (see T&Cs for more info).",
              value: "True"
            },
            {
              "@type": "PropertyValue",
              name: "Flexible Move Dates",
              description:
                "AnyVan offers low-cost car transport services for all vehicle models. This includes vehicle delivery and car shipping. Prices start from just £34, with nationwide services.",
              value: "True"
            }
          ],
          audience: {
            "@type": "PeopleAudience",
            audienceType: "Adults",
            description: "People who need car transport services",
            requiredMinAge: "16",
            suggestedMinAge: "18",
            suggestedMaxAge: "99"
          },
          offers: {
            "@type": "AggregateOffer",
            "@id": "https://www.anyvan.com/man-and-van/#offer",
            lowPrice: 26.0,
            priceCurrency: "GBP",
            acceptedPaymentMethod: ["Credit Card", "Debit Card"],
            offeredBy: {
              "@type": "Organization",
              name: "AnyVan",
              legalName: ["AnyVan Ltd", "AnyVan Ltd.", "AnyVan Limited"],
              "@id": "https://www.anyvan.com/#organization",
              url: "https://www.anyvan.com/",
              description:
                "AnyVan is a European online marketplace for users to obtain delivery, transport, and removal services from its community of transport partners, who must bid for the work.",
              image: { "@id": "https://www.anyvan.com/#logo" },
              logo: {
                "@type": "ImageObject",
                "@id": "https://www.anyvan.com/#logo",
                name: "AnyVan Logo",
                url: "https://www.anyvan.com/images/cust/AnyVanLogo.svg",
                contentUrl: "https://www.anyvan.com/images/cust/AnyVanLogo.svg",
                width: 140,
                height: 24
              },
              brand: { "@type": "Brand", name: "AnyVan" },
              foundingDate: "2009-03-04",
              sameAs: [
                "https://www.google.com/search?kgmid=/m/0fq2gmt",
                "https://www.google.com/search?kgmid=/g/1q6j3hd0z",
                "https://www.google.com/search?kgmid=/g/11s35rj2lf",
                "https://en.wikipedia.org/wiki/AnyVan",
                "https://www.wikidata.org/wiki/Q4778373",
                "https://www.facebook.com/AnyVanUK",
                "https://twitter.com/anyvan",
                "https://www.instagram.com/weareanyvan/",
                "https://www.linkedin.com/company/anyvan/",
                "https://www.crunchbase.com/organization/anyvan-com",
                "https://www.tiktok.com/@anyvan_uk",
                "https://www.youtube.com/@anyvanUK",
                "https://uk.pinterest.com/anyvan/",
                "https://99consumer.com/reviews/anyvan/",
                "https://uk.trustpilot.com/review/anyvan.com",
                "https://www.reviews.co.uk/company-reviews/store/anyvancom",
                "https://www.reviewcentre.com/Postal_and_Delivery_Services/AnyVan_-_wwwanyvancom-reviews_2323924"
              ],
              email: "info@anyvan.com",
              telephone: "+442038723050",
              acceptedPaymentMethod: ["Debit Card", "Credit Card"],
              aggregateRating: {
                "@type": "AggregateRating",
                worstRating: "1",
                bestRating: "5",
                ratingValue: trustpilotRating,
                ratingCount: trustpilotCustomers,
                url: "https://www.trustpilot.co.uk/review/anyvan.com"
              },
              address: {
                "@type": "PostalAddress",
                streetAddress: "5th Floor The Triangle, 5-17 Hammersmith Grove",
                addressRegion: "London",
                postalCode: "W6 0LG",
                addressCountry: "GB"
              },
              duns: "211556739",
              iso6523Code: "0060:211556739",
              identifier: [
                {
                  "@type": "PropertyValue",
                  propertyID: "Company Registration Number",
                  value: "06837274",
                  url: "https://find-and-update.company-information.service.gov.uk/company/06837274"
                },
                {
                  "@type": "PropertyValue",
                  propertyID: "OpenCorporates ID",
                  value: "06837274",
                  url: "https://opencorporates.com/companies/gb/06837274",
                  sameAs: "https://en.wikipedia.org/wiki/OpenCorporates"
                }
              ]
            },
            subjectOf: {
              "@type": "WebPage",
              name: "Car transport services",
              url: "https://www.anyvan.com/vehicle-delivery/car-delivery",
              potentialAction: {
                "@type": "QuoteAction",
                name: "Get a quote",
                description:
                  "Save money on heavy and large item courier services with AnyVan. ✓ Trusted moving experts ✓ Low costs ✓ Anywhere in the UK.",
                sameAs: [
                  "https://en.wikipedia.org/wiki/Request_for_quotation",
                  "https://www.wikidata.org/wiki/Q355618",
                  "https://www.google.com/search?kgmid=/m/027c69c"
                ],
                target: {
                  "@type": "EntryPoint",
                  urlTemplate: "https://www.anyvan.com/instant-prices/furniture/listing-items"
                },
                provider: {
                  "@type": "Organization",
                  name: "AnyVan",
                  "@id": "https://www.anyvan.com/#organization"
                }
              },
              mainContentOfPage: {
                "@type": "Table",
                name: "See how our car transport services prices compare",
                description:
                  "As the UK's most trusted mover, you can be sure you're in safe hands with AnyVan. And, to top it all off, we offer man and van services that are up to 40% cheaper than the competition. Of course, your man and van quote can depend on a few factors. But, here's how much it costs (on average!) to move some of your neighbour's most popular items.",
                cssSelector: {
                  "@type": "CssSelectorType",
                  "@value": "#comparison-block-1 > figure > table"
                }
              },
              about: {
                "@type": ["Product", "Service"],
                name: "Car transport services",
                "@id": "https://www.anyvan.com/vehicle-delivery/car-delivery",
                url: "https://www.anyvan.com/vehicle-delivery/car-delivery"
              },
              mentions: [
                {
                  "@type": "Thing",
                  name: "Sofa",
                  alternateName: ["Settee", "Couch"],
                  sameAs: [
                    "https://en.wikipedia.org/wiki/Couch",
                    "https://www.wikidata.org/wiki/Q131514",
                    "https://www.google.com/search?kgmid=/m/02crq1"
                  ]
                },
                {
                  "@type": "Thing",
                  name: "Unloading",
                  description:
                    "discharge a load, a freight or other goods from a transport vehicle or similar",
                  alternateName: "Unload",
                  sameAs: ["https://www.wikidata.org/wiki/Q12889429"]
                },
                {
                  "@type": "Thing",
                  name: "Loading",
                  alternateName: "Load",
                  description: "lifting a load up or putting it into something for transport",
                  sameAs: ["https://www.wikidata.org/wiki/Q69466762"]
                },
                {
                  "@type": "Thing",
                  name: "Insurance",
                  sameAs: [
                    "https://www.wikidata.org/wiki/Q43183",
                    "https://en.wikipedia.org/wiki/Insurance",
                    "https://www.google.com/search?kgmid=/m/03xks",
                    "https://www.wikidata.org/wiki/Q17093209"
                  ]
                }
              ],
              hasPart: {
                "@type": "FAQPage",
                breadcrumb: {
                  "@type": "BreadcrumbList",
                  itemListElement: [
                    {
                      "@type": "ListItem",
                      position: 1,
                      name: "Home",
                      item: "https://www.anyvan.com"
                    },
                    { "@type": "ListItem", position: 2, name: "Car transport services" }
                  ]
                },
                about: {
                  "@type": ["Product", "Service"],
                  name: "Car transport services",
                  "@id": "https://www.anyvan.com/vehicle-delivery/car-delivery",
                  url: "https://www.anyvan.com/vehicle-delivery/car-delivery"
                },
                author: {
                  "@type": "Organization",
                  "@id": "https://www.anyvan.com/#organization",
                  name: "AnyVan"
                },
                mainEntity: [
                  {
                    "@type": "Question",
                    name: "How much is a car transport service?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "Car transport costs depend on a bunch of things – like your car’s make and model, how far it’s going, when you want it moved, and the type of transporter you need. On average, car delivery costs around £3 per mile to travel under 50 miles. If you're transporting a car more than 50 miles, the average transport cost is around £1 per mile. Honestly, the easiest way to figure it out? Get an instant car transport quote.",
                      about: [
                        {
                          "@type": ["Product", "Service"],
                          name: "Car transport services",
                          "@id": "https://www.anyvan.com/vehicle-delivery/car-delivery",
                          url: "https://www.anyvan.com/vehicle-delivery/car-delivery"
                        },
                        {
                          "@type": "Thing",
                          name: "Request a Quote",
                          description: "Request a quotation for car delivery transport service",
                          sameAs: [
                            "https://en.wikipedia.org/wiki/Request_for_quotation",
                            "https://www.wikidata.org/wiki/Q355618",
                            "https://www.google.com/search?kgmid=/m/027c69c"
                          ]
                        }
                      ]
                    }
                  },
                  {
                    "@type": "Question",
                    name: "Can I hire someone to transport my car?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "Yes! When you book a vehicle transport service with us an experienced driver will take care of everything, including loading and unloading the car. We work with a network of professional transport providers across the UK (and even in Europe!), so no matter where you are, or where you’re looking to transport your car, we can help."
                    }
                  },
                  {
                    "@type": "Question",
                    name: "What is the most effective way to transport a car?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "Call us biased, but we’d say that the most effective way to transport a car is to hire a trusted professional who does these kinds of vehicle deliveries on a daily basis. Not only do our transport partners have years of experience, but they also have different transporters to suit your specific vehicle model’s needs. Let us handle it.",
                      about: [
                        {
                          "@type": "Thing",
                          name: "Unloading",
                          description:
                            "Discharge a load, a freight or other goods from a van or similar",
                          alternateName: "Unload",
                          sameAs: ["https://www.wikidata.org/wiki/Q12889429"]
                        },
                        {
                          "@type": "Thing",
                          name: "Loading",
                          alternateName: "Load",
                          description: "Lifting a load up or putting it into a van for transport",
                          sameAs: ["https://www.wikidata.org/wiki/Q69466762"]
                        },
                        {
                          "@type": "Thing",
                          name: "Packing Materials",
                          description: "Materials to ensure items are safe during transportation",
                          sameAs: [
                            "https://www.wikidata.org/wiki/Q66157003",
                            "https://www.google.com/search?kgmid=/g/121qg2_7",
                            "https://www.google.com/search?kgmid=/g/12111nx8"
                          ]
                        }
                      ]
                    }
                  },
                  {
                    "@type": "Question",
                    name: "How long will it take for a car to be delivered?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "You can choose car delivery and shipping times when booking your transport service. The distance travelled is a key factor here, as well as the availability of the transport provider. Luckily, we have the widest network of transport partners in the country and can usually do next day car delivery too.",
                      about: [
                        {
                          "@type": "Thing",
                          name: "Moving Company",
                          alternateName: "Removals Company",
                          sameAs: [
                            "https://en.wikipedia.org/wiki/Moving_company",
                            "https://www.wikidata.org/wiki/Q1757263",
                            "https://www.wikidata.org/wiki/Q2918584",
                            "https://en.wikipedia.org/wiki/Relocation_(personal)"
                          ]
                        }
                      ]
                    }
                  },
                  {
                    "@type": "Question",
                    name: "How much does it cost to transport a car 300 miles?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "On average, a car transport quote of 300 miles would be around £300 – this is based on an average cost of £1 per mile. Different factors, like date, time and vehicle model, can affect this though. The easiest thing to do is to get an instant quote."
                    }
                  },
                  {
                    "@type": "Question",
                    name: "Is the car covered during transit?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "Yes, it is! Every AnyVan car transport service includes compensation cover up to £50K. This is fire and theft cover. If you’re keen on the details check out our terms and conditions.",
                      about: {
                        "@type": "Thing",
                        name: "Additional Charges",
                        sameAs: [
                          "https://www.wikidata.org/wiki/Q230696",
                          "https://www.google.com/search?kgmid=/g/120x5qy6"
                        ]
                      }
                    }
                  },
                  {
                    "@type": "Question",
                    name: "How do I pick the best car transport company for my needs?",
                    acceptedAnswer: {
                      "@type": "Answer",
                      text: "Choosing a car transport company can take time, research and phone calls. But with AnyVan, we handle that for you! We work with transport partners across the UK. Just let us know what vehicle you’re shipping and where it’s going, and we’ll match you with the right provider for your needs.",
                      about: {
                        "@type": "Thing",
                        name: "Additional Charges",
                        sameAs: [
                          "https://www.wikidata.org/wiki/Q230696",
                          "https://www.google.com/search?kgmid=/g/120x5qy6"
                        ]
                      }
                    }
                  }
                ]
              }
            }
          }
        })

        const routeWithLocaleToSchema = {
          [LOCALE.UK]: {
            "/home": homepageSchemaUK,
            "": homepageSchemaUK,
            "/av/home-clone": homepageSchemaUK,
            "/home-generic-variant": homepageSchemaUK,
            "/removals": removalsPageSchema,
            "/man-and-van": manAndVanSchema,
            "/courier-services/heavy-large-item-couriers": heavyAndLargeItemCouriersSchema,
            "/vehicle-delivery/car-delivery": vehicleDeliverySchema
          }
        }

        if (!props.noSeo) {
          const existingIndex = head.script.findIndex(
            script => script.type === "application/ld+json"
          )

          const currentLocale = i18n.locale.value
          const currentPath = getPath(route)
          const customSchema = routeWithLocaleToSchema[currentLocale]?.[currentPath]
          const fallbackSchema = JSON.stringify(buildStructuredData(props.page))

          console.log("[Schema] Using:", customSchema ? "custom" : "fallback")

          const schemaData = {
            type: "application/ld+json",
            key: `schema-org-${currentPath || "home"}`,
            tagPriority: 1,
            innerHTML: customSchema ?? fallbackSchema
          }

          head.script.push(schemaData)
        }

        head.title = props.page?.title
      }

      if (
        [
          "PageLayoutAdvice",
          "PageLayoutBlog",
          "PageLayoutDefault",
          "PageLayoutRightSidebar"
        ].includes(props.layout) &&
        props.page?.featuredImage?.node
      ) {
        link.push({
          key: "preload-featured-image",
          rel: "preload",
          as: "image",
          href: replaceUrlS3(props.page.featuredImage.node.sourceUrl, config.public)
        })
      }

      const canonical = props.page?.seo?.canonical
      const noindex = props.page?.seo?.metaRobotsNoindex === "noindex"
      const seoPage = !noindex

      if (seoPage && canonical && config.public.frontendHost) {
        link.push({
          key: "canonical",
          rel: "canonical",
          href: canonical.includes(config.public.cmsHost)
            ? canonical
                .replace(config.public.cmsHost, config.public.frontendHost)
                .replace(/\/$/, "")
            : canonical.replace(/\/$/, "")
        })
      }

      head.link = [...link]
      return head
    })()

    useHead(headConfig)

    return {
      config,
      i18n,
      route
    }
  },

  data(): ComponentData {
    return {
      layout: "",
      internalReviewsFilter: {}
    }
  },

  async fetch() {
    await this.setBreadcrumbs({
      breadcrumbs: this.page?.seo?.breadcrumbs
    })
  },

  computed: {
    ...mapGetters("auth", {
      nonce: GET_FW_TOKEN
    })
  },

  created() {
    const hasLatLongs =
      this.page &&
      this.page.location &&
      this.page.location.mapPinsLatitude &&
      this.page.location.mapPinsLongitude

    if (hasLatLongs) {
      this.setMapLocations(this.page.location)
      this.setLocationInfo(this.page.location)
    }

    if (this.page && this.page.pageInternalReviews) {
      this.setInternalReviews(this.page.pageInternalReviews)
    }

    this.layout = this.getLayoutFromTemplate()
    this.internalReviewsFilter = this.page.internalReviews
  },

  async mounted() {
    console.log("Hello, Nuxt 3 🐣")
    const query = this.route.query
    query.utm_media =
      query.utm_media || (this.page!.seo!.metaRobotsNoindex === "index" ? "seo" : "ppc")

    await this.authenticateAndUpdatePhone()
    await this.sendTrackingEvents()
    await this.useCreditCode()
  },

  methods: {
    replaceUrlS3,

    ...mapActions("pages", {
      setBreadcrumbs: SET_BREADCRUMBS
    }),
    ...mapActions("location", {
      setMapLocations: SET_MAP_LOCATIONS,
      setLocationInfo: SET_LOCATION_INFO
    }),
    ...mapActions("internal-reviews", {
      setInternalReviews: SET_INTERNAL_REVIEWS
    }),
    ...mapActions("auth", {
      setFwToken: SET_FW_TOKEN,
      setUser: SET_USER
    }),
    ...mapActions("phone-number", {
      setPhoneNumberValue: SET_PHONE_NUMBER_VALUE
    }),
    ...mapActions("credit-code", {
      setCreditCodeValue: SET_CREDIT_CODE_VALUE,
      setCreditCodeLocale: SET_CREDIT_CODE_LOCALE
    }),

    getLayoutFromTemplate(): string {
      let template =
        this.page &&
        this.page.template &&
        this.page.template.templateName
          .trim()
          .replace(/[-_\s]+(.)?/g, (_, c) => (c ? c.toUpperCase() : ""))

      if (
        ![
          "Advice",
          "Article",
          "Avb",
          "Blank",
          "Blog",
          "Default",
          "RemovalsHub",
          "RightSidebar",
          "StaticDocument",
          "Rebrand"
        ].includes(template)
      ) {
        template = "Default"
      }

      return "PageLayout" + template
    },

    async authenticateAndUpdatePhone(): Promise<void> {
      const auth = new AuthenticationService(this.$axios, this.config.public)
      const profile = await auth.getProfile()
      if (profile && profile.fw_token) {
        await this.setFwToken(profile.fw_token)
        await this.updatePhoneNumber()
      }
      if (profile && profile.user) {
        await this.setUser(profile.user)
      }
    },

    async updatePhoneNumber(): Promise<void> {
      const phoneService = new PhoneNumberService(this.$axios, this.config.public)

      if (this.page && this.route && this.nonce) {
        const phoneNumber: PhoneNumberPair = await phoneService.updatePhoneNumber(
          this.page,
          this.route,
          this.nonce,
          this.i18n.locale.value
        )
        await this.setPhoneNumberValue(phoneNumber)

        const trackingData: CallUsPayload = {
          phoneNumber: phoneNumber.base,
          label: "lego_phone_number",
          action: "viewed"
        }
        if (phoneNumber.extension !== "") {
          // @ts-ignore
          trackingData.extension = phoneNumber.extension
        }
        this.$snowplow.trackCallUs(trackingData)
      }
    },

    async useCreditCode(): Promise<void> {
      const creditCodeService = new CreditCodeService(this.$axios, this.config.public)
      const creditCodeResponse = await creditCodeService.getCreditCodeValue(this.page, this.route)
      if (creditCodeResponse && creditCodeResponse.value) {
        this.setCreditCodeValue(parseInt(creditCodeResponse.value))
        this.setCreditCodeLocale(creditCodeResponse.locale)
      }
    },

    async sendTrackingEvents(): Promise<void> {
      const trackingService = new TrackingService(this.$axios, this.config.public)
      const response = await trackingService.postTrackingEvent(
        { label: "LEGO - " + this.route.path },
        this.route,
        useCookie("PHPSESSID"),
        this.page,
        window
      )

      if (response && response.data && response.data.track_id) {
        this.$snowplow.trackAnyVanTrackId(response.data.track_id)
      }
    }
  }
})
</script>
